import React from 'react';

import { format, formatISO, startOfQuarter } from 'date-fns';

import { FEE_ENTRY_TYPES } from 'components/CMASummary/constants';
import { isCommunityCCRC } from 'components/Community/utils';
import { FREQUENCY_OPTIONS } from 'components/CommunityInfo/careTypes/constants';
import { FEES_PRICING_TYPES } from 'constants/careTypes';
import {
  CHART_TYPE,
  LABEL_FONT_COLOR,
  LABEL_FONT_FAMILY,
  LABEL_FONT_FAMILY_COMMUNITY,
  LABEL_FONT_SIZE,
} from 'constants/charts';
import { COMPANY_COMMUNITY_RELATION_TYPE } from 'constants/company';

import ChartTooltip from './ChartTooltip';
import {
  DUPLICATE_SCATTER_POINT_OFFSET,
  SCATTER_DISPLAY_CATEGORY_SCORE_FIELD,
  SCATTER_DISPLAY_SCORE_FIELD,
} from './constants';
import { DataItemType } from './types';
import {
  SWOT_COMPARISON_LABELS,
  SWOT_COMPARISON_TYPES,
  SWOT_SUBCATEGORY_SCORE_RANGES,
  WIDGET_TYPES,
} from '../constants';
import { valueFormatter } from '../DataTable/utils';
import { formatValueDifference, getDifferenceColor } from '../valueDifferenceUtils';

const toFiniteNumber = (value: any) => {
  const numberValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

export const getDisplayScatterData = (data: any[] = []) => {
  const groups = new Map<string, any[]>();

  data.forEach((item) => {
    const score = toFiniteNumber(item?.score);
    const categoryScore = toFiniteNumber(item?.categoryScore);
    const key = `${score ?? 'null'}-${categoryScore ?? 'null'}`;

    groups.set(key, [...(groups.get(key) ?? []), item]);
  });

  return data.map((item) => {
    const score = toFiniteNumber(item?.score);
    const categoryScore = toFiniteNumber(item?.categoryScore);
    const key = `${score ?? 'null'}-${categoryScore ?? 'null'}`;
    const group = groups.get(key) ?? [];

    if (score === null || categoryScore === null || group.length <= 1) {
      return {
        ...item,
        [SCATTER_DISPLAY_SCORE_FIELD]: score,
        [SCATTER_DISPLAY_CATEGORY_SCORE_FIELD]: categoryScore,
      };
    }

    const index = group.indexOf(item);
    const centeredIndex = index - (group.length - 1) / 2;

    return {
      ...item,
      [SCATTER_DISPLAY_SCORE_FIELD]: score,
      [SCATTER_DISPLAY_CATEGORY_SCORE_FIELD]: categoryScore + centeredIndex * DUPLICATE_SCATTER_POINT_OFFSET,
    };
  });
};

export const labelFormatter = (value: any, widgetType: number, unitSource?: any) => {
  if (value == null) {
    return '';
  }

  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) {
    return '';
  }

  return valueFormatter(numericValue, widgetType, true, undefined, unitSource);
};

export const formatYAxisValue = (text: string, widgetType: number, unitSource?: any) => {
  if (text === '0') {
    return text;
  }

  return valueFormatter(+text, widgetType, true, undefined, unitSource);
};

export const axisLabelFormatter = (item: any, widgetType: number) => {
  if (!item) {
    return '';
  }

  const date = new Date(item);
  if (Number.isNaN(date.getTime())) return '';

  const dateFormat = widgetType === WIDGET_TYPES.RATING ? 'MMM dd' : 'qqq yyyy';
  return format(date, dateFormat);
};

export const buildCommunityFeesText = (fees: any[], livingTypes: any[] = [], max = 40) => {
  if (!fees?.length) return '';

  const grouped: Record<string, string[]> = {};

  fees
    .filter((fee) => fee.frequency != null)
    .forEach((fee) => {
      const livingType = livingTypes.find((lt) => lt?.id === fee.living_type);
      const abv = livingType?.abv;
      if (!abv) return;

      const predefined = FREQUENCY_OPTIONS.find((item) => item.value === fee.frequency)?.label;

      const label = predefined ?? `Equal to ${(fee.frequency || 0) * 100}% of month's rent`;

      if (!grouped[label]) {
        grouped[label] = [];
      }

      grouped[label].push(abv);
    });

  return Object.entries(grouped)
    .map(([label, abvs]) => `${abvs.join(', ')}: ${label}`)
    .join(', ');
};

export const getSanitizedContractLabel = (community?: any) => {
  if (!community?.contract_label) {
    return '';
  }

  // Antd Plot charts don't support having axis labels split into two rows with different styles.
  // So until we figure out a workaround (probably custom annotations with contract rows positioned
  // below the community name) we will just show the contract type with the same style of the community name.
  // Also, since the contract type for some reason gets truncated, we will remove the `Contract Type ` string and
  // only leave the `A 55%` part.
  // Also we will manually remove 0% from rental.
  return community.contract_label.replace('Contract Type ', '').replace('Rental 0%', 'Rental');
};

export const buildFeesText = (
  fees: any[],
  livingTypes: any[] = [],
  feePricingTypes: any[] = [],
  showLevelsCount: boolean
) => {
  if (!fees?.length) return '';

  return fees
    .map((fee) => {
      const feeType = feePricingTypes.find((ft) => ft?.id === fee.pricing_type_id);
      const livingType = livingTypes.find((lt) => lt?.id === fee.living_type_id);

      if (feeType?.id === FEES_PRICING_TYPES.LEVELS && fee.highest_levels_count >= 0 && showLevelsCount) {
        return `${livingType?.abv}: Level (${fee.highest_levels_count})`;
      } else if (feeType) {
        return `${livingType?.abv}: ${feeType.type}`;
      }
      return '';
    })
    .filter(Boolean)
    .join(', ');
};

export const buildYAxisLabel = ({
  text,
  item,
  index,
  data,
  type,
  livingTypes,
  feePricingTypes,
  showLevelsCount,
}: {
  text: string;
  item: any;
  index: number;
  data: any[];
  type: number;
  livingTypes: any[];
  feePricingTypes: any[];
  showLevelsCount: boolean;
}) => {
  const community = data.find((c: any) => text === c.community_name);
  const dataItem = data[index];
  const contractText = getSanitizedContractLabel(community);
  const lines = [text];

  if (type === WIDGET_TYPES.COMMUNITY_FEES_GRAPH && dataItem?.fees?.length) {
    const feesText = buildCommunityFeesText(dataItem.fees, livingTypes);
    if (feesText) {
      lines.push(feesText);
    }
  }

  if (
    [
      WIDGET_TYPES.CARE_FEES_GRAPH,
      WIDGET_TYPES.MEDICATION_FEES_GRAPH,
      WIDGET_TYPES.MARKET_RATE_AVERAGE_CARE_COST_GRAPH,
    ].includes(type) &&
    dataItem?.fees?.length
  ) {
    const feesText = buildFeesText(dataItem.fees, livingTypes, feePricingTypes, showLevelsCount);
    if (feesText) {
      lines.push(feesText);
    }
  }

  if (type === WIDGET_TYPES.MARKET_RATE_CARE_MEDICATION_COST_GRAPH && dataItem?.fees?.length) {
    const careFees = dataItem?.fees.filter((x: any) => x.fee_type === FEE_ENTRY_TYPES.CARE_FEES);
    if (careFees?.length) {
      const feesText = buildFeesText(careFees, livingTypes, feePricingTypes, showLevelsCount);
      if (feesText) {
        lines.push(`CARE - ${feesText}`);
      }
    }
    const medFees = dataItem?.fees.filter((x: any) => x.fee_type === FEE_ENTRY_TYPES.MEDICATION_FEE);
    if (medFees?.length) {
      const feesText = buildFeesText(medFees, livingTypes, feePricingTypes, showLevelsCount);
      if (feesText) {
        lines.push(`MED ${feesText}`);
      }
    }
  }

  if (contractText) {
    lines.push(contractText);
  }

  return lines.filter(Boolean).join('\n');
};

export const tooltipFormatter = (item: any, widgetType: number, valueDisplay?: number, unitSource?: any) => {
  const formattedValue = valueFormatter(item?.value, widgetType, true, undefined, unitSource);
  const formattedDifference = formatValueDifference(
    item?.data?.difference ?? item?.difference,
    widgetType,
    valueDisplay,
    unitSource
  );

  return {
    name: item.community_name,
    value: formattedDifference ? `${formattedValue} ${formattedDifference}`.trim() : formattedValue,
  };
};

export const tooltipSorter = (originalItems: any) => {
  return originalItems.sort((a: any, b: any) => a.data.value - b.data.value);
};

export const tooltipContent = (
  widgetType: number,
  data: any,
  type: string = CHART_TYPE.BAR,
  valueDisplay?: number,
  communityUnitSource?: any
) => {
  return (
    <ChartTooltip
      widgetType={widgetType}
      data={data}
      type={type}
      valueDisplay={valueDisplay}
      communityUnitSource={communityUnitSource}
    />
  );
};

const getComparisonLabel = (score?: number) => {
  if (typeof score !== 'number' || Number.isNaN(score)) {
    return SWOT_COMPARISON_LABELS[SWOT_COMPARISON_TYPES.SIMILAR];
  }

  const matchedRange = SWOT_SUBCATEGORY_SCORE_RANGES.find((range) => score <= range.max);
  const comparisonType = matchedRange?.comparisonType ?? SWOT_COMPARISON_TYPES.SIMILAR;
  return SWOT_COMPARISON_LABELS[comparisonType];
};

const getSubcategoryComparisonLabel = (score?: number) => {
  if (typeof score !== 'number' || Number.isNaN(score)) {
    return 'N/A';
  }

  const matchedRange = SWOT_SUBCATEGORY_SCORE_RANGES.find((range) => score <= range.max);
  const comparisonType = matchedRange?.comparisonType ?? SWOT_COMPARISON_TYPES.SIMILAR;
  return SWOT_COMPARISON_LABELS[comparisonType];
};

const getComparisonColor = (label: string) => {
  if (label === 'N/A') {
    return 'var(--gray-dark)';
  }
  if (label.includes('Better') || label === 'Similar') {
    return 'var(--positive-green)';
  }
  return 'var(--dark-orange)';
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const SCATTER_TOOLTIP_STYLES = {
  container:
    'background: var(--background-gray); border:1px solid var(--line-gray); box-shadow:0 2px 10px rgba(0,0,0,0.08); overflow:hidden;',
  titleSection:
    'padding:0 10px; border-bottom:1px solid var(--line-gray); font-size:10px; font-family:var(--font-bold); color:var(--text-primary);',
  summarySection: 'padding:10px 10px 5px; font-size:10px; color: var(--gray-dark);',
  summaryStatus: 'font-family:var(--font-bold);',
  scoreSection:
    'padding:0 10px 10px; border-bottom:1px solid var(--line-gray); display:flex; justify-content:space-between;',
  scoreLabel: 'font-size:10px; color:var(--text-primary);',
  scoreValue: 'font-size:10px; font-family:var(--font-bold); color: var(--text-primary);',
  subcategoriesSection: 'padding:0 10px;',
  subcategoriesHeader: 'display:flex; justify-content:space-between; margin:8px 0;',
  subcategoriesHeaderLabel: 'font-size:10px; font-family:var(--font-bold); letter-spacing:0.02em; var(--text-primary);',
  subcategoryRow: 'display:flex; justify-content:space-between; gap:16px; margin-bottom:2px;',
  subcategoryName: 'color:var(--text-primary); font-size:10px;',
  subcategoryStatus: 'font-size:10px; white-space:nowrap;',
  subcategoryScore: 'color:var(--gray-dark);',
} as const;

const renderSubcategoryRow = (name: string, label: string, color: string, shownScore: string) => `
  <div style="${SCATTER_TOOLTIP_STYLES.subcategoryRow}">
    <div style="${SCATTER_TOOLTIP_STYLES.subcategoryName}">${name}</div>
    <div style="${SCATTER_TOOLTIP_STYLES.subcategoryStatus}; color:${color};">
      ${label} <span style="${SCATTER_TOOLTIP_STYLES.subcategoryScore}">(${shownScore})</span>
    </div>
  </div>
`;

export const buildScatterTooltipContent = (_title: string, items: any[]) => {
  const point = items?.[0]?.data || {};
  const categoryName = point?.categoryName || 'Category';
  const parsedMarketCompetitionScore = typeof point?.score === 'number' ? point.score : Number(point?.score);
  const hasMarketCompetitionScore = Number.isFinite(parsedMarketCompetitionScore);
  const marketCompetitionScore = hasMarketCompetitionScore ? parsedMarketCompetitionScore : null;
  const comparisonLabel =
    marketCompetitionScore == null ? 'N/A' : getComparisonLabel(marketCompetitionScore);
  const comparisonColor = getComparisonColor(comparisonLabel);
  const subcategories = Array.isArray(point?.subcategories) ? point.subcategories : [];

  const subcategoriesMarkup = subcategories
    .map((item: any) => {
      const name = escapeHtml(item?.name || item?.label || 'Subcategory');
      const parsedSubScore = typeof item?.score === 'number' ? item.score : Number(item?.score);
      const hasSubScore = Number.isFinite(parsedSubScore);
      const subLabel = hasSubScore ? getSubcategoryComparisonLabel(parsedSubScore) : 'N/A';
      const subColor = getComparisonColor(subLabel);
      const shownScore = hasSubScore ? parsedSubScore.toFixed(2) : '--';

      return renderSubcategoryRow(name, subLabel, subColor, shownScore);
    })
    .join('');

  const showSubcategorySection = subcategories.length > 0;

  return `
    <div style="${SCATTER_TOOLTIP_STYLES.container}">
      <div style="${SCATTER_TOOLTIP_STYLES.titleSection}">
        ${escapeHtml(categoryName)}
      </div>
      <div style="${SCATTER_TOOLTIP_STYLES.summarySection}">
        In comparison to competitors, I am doing <span style="${SCATTER_TOOLTIP_STYLES.summaryStatus}; color:${comparisonColor};">${comparisonLabel.toLowerCase()}</span>.
      </div>
      <div style="${SCATTER_TOOLTIP_STYLES.scoreSection}">
        <span style="${SCATTER_TOOLTIP_STYLES.scoreLabel}">Score</span>
        <span style="${SCATTER_TOOLTIP_STYLES.scoreValue}">${
          marketCompetitionScore == null ? '--' : marketCompetitionScore.toFixed(2)
        }</span>
      </div>
      ${
        showSubcategorySection
          ? `
            <div style="${SCATTER_TOOLTIP_STYLES.subcategoriesSection}">
              <div style="${SCATTER_TOOLTIP_STYLES.subcategoriesHeader}">
                <span style="${SCATTER_TOOLTIP_STYLES.subcategoriesHeaderLabel}">SUBCATEGORIES</span>
                <span style="${SCATTER_TOOLTIP_STYLES.subcategoriesHeaderLabel}">MY COMMUNITY IS</span>
              </div>
              ${subcategoriesMarkup}
            </div>
          `
          : ''
      }
    </div>
  `;
};

export const formatTooltipDate = (itemData: any, widgetType: number) => {
  const rawDate = itemData?.quarterDate ?? itemData?.date;

  if (!rawDate) {
    return '';
  }

  const parsedDate = new Date(rawDate);

  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  const isRatingGraph = widgetType === WIDGET_TYPES.RATING_GRAPH;
  return format(parsedDate, isRatingGraph ? 'qqq yyyy' : 'MMM d');
};

const addQuarterDates = (inputData: DataItemType[]): DataItemType[] => {
  if (!Array.isArray(inputData)) {
    return inputData;
  }

  return inputData.map((item) => {
    if (!item.date) return item;

    const quarterDate = formatISO(startOfQuarter(new Date(item.date)));

    return {
      ...item,
      quarterDate,
    };
  });
};

const toTimestamp = (value?: string) => {
  if (!value) {
    return Number.NEGATIVE_INFINITY;
  }

  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
};

const aggregateRatingsByQuarter = (dataWithQuarter: DataItemType[]) => {
  type AggregatedEntry = {
    sum: number;
    count: number;
    total?: number;
    sample: DataItemType;
  };

  const aggregatedMap = new Map<string, AggregatedEntry>();

  dataWithQuarter.forEach((item) => {
    if (!item.quarterDate) {
      return;
    }

    const numericValue = Number(item.value);
    if (Number.isNaN(numericValue)) {
      return;
    }

    const mapKey = `${item.community_id ?? item.community_name}-${item.quarterDate}`;
    const aggregated = aggregatedMap.get(mapKey);
    const numericTotal = item.total != null && !Number.isNaN(Number(item.total)) ? Number(item.total) : undefined;

    if (aggregated) {
      aggregatedMap.set(mapKey, {
        sum: aggregated.sum + numericValue,
        count: aggregated.count + 1,
        total:
          aggregated.total != null || numericTotal != null ? (aggregated.total ?? 0) + (numericTotal ?? 0) : undefined,
        sample: aggregated.sample,
      });
    } else {
      aggregatedMap.set(mapKey, {
        sum: numericValue,
        count: 1,
        total: numericTotal,
        sample: item,
      });
    }
  });

  const aggregatedData = Array.from(aggregatedMap.values())
    .map(({ sum, count, total, sample }) => ({
      ...sample,
      date: sample.quarterDate ?? sample.date,
      value: count > 0 ? sum / count : sample.value,
      total: total ?? sample.total,
    }))
    .sort((a, b) => {
      const dateDiff = toTimestamp(a.quarterDate) - toTimestamp(b.quarterDate);

      if (dateDiff !== 0) {
        return dateDiff;
      }

      return (a.community_name ?? '').localeCompare(b.community_name ?? '');
    });

  const withoutQuarter = dataWithQuarter.filter((item) => !item.quarterDate);

  return [...aggregatedData, ...withoutQuarter];
};

export const normalizeLineChartData = (widgetType: number, inputData: DataItemType[]): DataItemType[] => {
  const dataWithQuarter = addQuarterDates(inputData);

  if (widgetType !== WIDGET_TYPES.RATING_GRAPH) {
    return dataWithQuarter;
  }

  // Ratings widgets receive individual review entries, so we need to aggregate them so charts show a single point per quarter.
  return aggregateRatingsByQuarter(dataWithQuarter);
};

export const textStyle = (title: string, data: any) => {
  const isCommunity = data.find(
    (item: any) =>
      title?.includes(item.community_name) && item.community_type == COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY
  );

  const fontFamily = isCommunity ? LABEL_FONT_FAMILY_COMMUNITY : LABEL_FONT_FAMILY;
  return { fill: LABEL_FONT_COLOR, fontFamily: fontFamily, fontSize: LABEL_FONT_SIZE };
};

export const communityNameAndContractText = (communityName: string, data: any[]) => {
  const community = data.find((item: any) => communityName === item.community_name);
  const contractLabel = getSanitizedContractLabel(community);

  if (!contractLabel) {
    return communityName;
  }
  return `${communityName}\n${contractLabel}`;
};

/*
 * Difference Label Coloring for Ant Design Plot Charts (GRAPH now and grouped charts)
 *
 * Problem:
 * Ant Design Plot (@ant-design/plots) does not support rendering HTML or custom React components
 * inside chart data labels. The label `formatter` prop only accepts a string return value, which means
 * we cannot use styled spans or divs to color parts of the label differently.
 *
 * Solution:
 * We manually manipulate the SVG DOM after the chart renders. The chart uses SVG renderer, so all
 * labels are rendered as <text> elements. We find these elements and split them into multiple
 * <tspan> elements with different fill colors.
 *
 * How it works:
 * 1. Access the SVG element via `plot.chart.ele` (G2 chart instance) or fallback to querying
 *    the container for an <svg> element.
 *
 * 2. Find all <text> elements and check if they match our label pattern using the regex:
 *    /^(.+?)\s([+-]\$?[\d,.]+(?:%| sq (?:ft|m))?)$/
 *    - (.+?) captures the base value non-greedily (e.g., "$4,000")
 *    - \s matches the space separator
 *    - ([+-]\$?[\d,.]+(?:%| sq (?:ft|m))?) captures the difference value: a sign (+/-), optional $, digits/commas/dots,
 *      optional % or " sq ft"/"sq m" suffix (e.g., "-$1,545", "+15%", "-1.5", "+263 sq ft").
 *      This avoids matching axis labels that contain " - " in text.
 *
 * 3. Replace the text content with two <tspan> elements:
 *    - First <tspan>: base value in default color
 *    - Second <tspan>: difference value in green (positive) or orange (negative)
 *
 * 4. Use MutationObserver to re-apply coloring when the chart re-renders (e.g., on data/filter changes).
 *
 * Note on document.createElementNS:
 * SVG elements must be created with their XML namespace URI. Unlike HTML elements which use
 * document.createElement(), SVG elements require document.createElementNS() with the SVG namespace
 * 'http://www.w3.org/2000/svg'. Without the correct namespace, the browser won't recognize the
 * element as a valid SVG element and it won't render.
 */

const POSITIVE_COLOR = '#0ab776';
const NEGATIVE_COLOR = '#fa7550';
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const DIFFERENCE_LABEL_PATTERN = /^(.+?)\s([+-]\$?[\d,.]+(?:%| sq (?:ft|m))?)$/;

const applyDifferenceColoring = (svgElement: Element, widgetType?: number) => {
  const textElements = svgElement.querySelectorAll('text');

  textElements.forEach((textEl: SVGTextElement) => {
    if (textEl.querySelector('tspan[fill]')) {
      return;
    }

    const textContent = textEl.textContent || '';
    const match = textContent.match(DIFFERENCE_LABEL_PATTERN);

    if (!match) {
      return;
    }

    const [, baseValue, differenceValue] = match;
    const isNegative = differenceValue.startsWith('-');
    const numericDifference = Number(differenceValue.replace(/[^0-9.-]/g, ''));
    const color = Number.isFinite(numericDifference)
      ? getDifferenceColor(numericDifference, widgetType)
      : isNegative
      ? NEGATIVE_COLOR
      : POSITIVE_COLOR;

    while (textEl.firstChild) {
      textEl.removeChild(textEl.firstChild);
    }

    const baseTspan = document.createElementNS(SVG_NAMESPACE, 'tspan');
    baseTspan.textContent = baseValue + ' ';
    textEl.appendChild(baseTspan);

    const diffTspan = document.createElementNS(SVG_NAMESPACE, 'tspan');
    diffTspan.textContent = differenceValue;
    diffTspan.setAttribute('fill', color);
    textEl.appendChild(diffTspan);
  });
};

export const setupDifferenceColorObserver = (plot: any, widgetType?: number): (() => void) | undefined => {
  const svgElement = plot?.chart?.ele || plot?.container?.querySelector('svg');

  if (!svgElement) {
    return undefined;
  }

  applyDifferenceColoring(svgElement, widgetType);

  const observer = new MutationObserver(() => {
    applyDifferenceColoring(svgElement, widgetType);
  });

  observer.observe(svgElement, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  return () => observer.disconnect();
};

export const mergeCommunityFees = (data: any[]) => {
  const communityMap = new Map<number, any>();

  for (const row of data) {
    const id = row.community_id;

    if (!communityMap.has(id)) {
      communityMap.set(id, {
        ...row,
        fees: [],
      });
    }

    const community = communityMap.get(id);

    if (Array.isArray(row.fees)) {
      for (const fee of row.fees) {
        const exists = community.fees.some(
          (f: any) =>
            f.pricing_type_id === fee.pricing_type_id &&
            f.living_type_id === fee.living_type_id &&
            f.fee_type === fee.fee_type
        );

        if (!exists) {
          community.fees.push(fee);
        }
      }
    }
  }

  return [...communityMap.values()];
};
